'use client';

import React, { useMemo, useCallback, useState } from 'react';
import { useRouter, useParams, usePathname } from 'next/navigation';
import { useAuth } from 'src/context/auth';
import { useIntegrations } from '../../Integrations/context';
import { useGetAllCompaniesQuery, useGetCompanyUsersQuery, useGetCompanyByIdQuery } from 'src/api/redux';
import { useDebounce } from 'use-debounce';
import Dropdown, { DropdownItem } from './components/Dropdown/Dropdown';
import BreadcrumbLink from './components/BreadcrumbLink/BreadcrumbLink';
import { DROPDOWN_TYPES, DropdownType } from './constants';
import { useSettingsRoutes } from 'src/hooks/settings';
import { useAuthorization } from 'src/hooks/authorization';

interface BreadcrumbItem {
  label: string;
  href: string;
  isDropdown?: boolean;
  dropdownType?: DropdownType;
  dropdownItems?: DropdownItem[];
  isActive?: boolean;
  onClick?: () => void;
}

const getCompanyBreadcrumb = (
  companyName?: string,
  companyId?: number,
  hasCompanyAccess?: boolean,
  fallbackPath?: string
): BreadcrumbItem => ({
  label: companyName || 'Settings',
  // Only link to company page if user has access, otherwise use fallback
  href: hasCompanyAccess ? `/settings/company/${companyId}` : (fallbackPath ?? `/settings/company/${companyId}/preferences`),
});

const SettingsBreadcrumbs: React.FC = () => {
  const { user } = useAuth();
  const { mappings, setCurrentMapping } = useIntegrations();
  const router = useRouter();
  const pathname = usePathname();
  const { companyId, userId } = useParams<{ companyId: string; userId: string }>();
  const { routes: settingsRoutes } = useSettingsRoutes();
  const { userManagementCapabilities } = useAuthorization();
  const { canView: canViewCompanyUsers } = userManagementCapabilities;

  // Search states for dropdowns
  const [companySearchTerm, setCompanySearchTerm] = useState('');
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [debouncedCompanySearch] = useDebounce(companySearchTerm, 300);
  const [debouncedUserSearch] = useDebounce(userSearchTerm, 300);

  const { data: companyData } = useGetAllCompaniesQuery({ 
    pageSize: 1000,
    ...(debouncedCompanySearch && { q: debouncedCompanySearch })
  }); // Search companies with debounced query
  
  const { data: userData } = useGetCompanyUsersQuery(
    { 
      companyId, 
      pageSize: 1000,
      ...(debouncedUserSearch && { q: debouncedUserSearch })
    }, // Search users with debounced query
    { skip: !companyId }
  );
  
  // Get specific company data if not found in the all companies list
  const { data: currentCompanyData } = useGetCompanyByIdQuery(
    { companyId },
    { skip: !companyId }
  );

  const getBreadcrumbs = useCallback((): BreadcrumbItem[] => {
    const items: BreadcrumbItem[] = [];
    const path = pathname;

    // Handle special sections (Preferences, Integrations, Admin)
    if (
      path.includes('/preferences') ||
      path.includes('/integrations') ||
      path.includes('/admin')
    ) {
      // Only link back to the company page if the viewer can actually open it —
      // staff and License Managers alike (MKT-411).
      const fallbackPath = settingsRoutes[0]?.path;
      items.push(
        getCompanyBreadcrumb(
          user?.company?.name,
          user?.company?.id,
          canViewCompanyUsers,
          fallbackPath
        )
      );

      if (path.includes('/preferences')) {
        items.push({
          label: 'Preferences',
          href: `/settings/company/${user?.company?.id}/preferences`,
        });
      } else if (path.includes('/integrations')) {
        items.push({
          label: 'Integrations',
          href: `/settings/company/${user?.company?.id}/integrations`,
        });
        // Add additional integration routes if needed
        if (path.includes('/integrations/')) {
          const integrationPath = path.split('/integrations/')[1];
          const [integrationName, ...rest] = integrationPath.split('/');

          items.push({
            label: integrationName,
            href: `/settings/company/${user?.company?.id}/integrations/${integrationName}`,
          });

          // If we're in the mappings section or viewing a specific mapping, add the mappings dropdown
          if (rest[0] === 'mappings' || (rest[0] && /^\d+$/.test(rest[0]))) {
            // Filter mappings for the current integration
            const integrationMappings = mappings?.filter(
              (mapping) => mapping.crm?.toLowerCase() === integrationName
            );

            // Add the current mapping with dropdown
            if (rest[0] && /^\d+$/.test(rest[0])) {
              const mappingId = rest[0];
              const currentMapping = integrationMappings?.find(
                (m) => m.id.toString() === mappingId
              );
              if (currentMapping) {
                const mappingPath = `/settings/company/${user?.company?.id}/integrations/${integrationName}/${mappingId}`;
                const isCurrentPath = pathname === mappingPath;
                items.push({
                  label: currentMapping.label,
                  href: mappingPath,
                  isActive: isCurrentPath,
                  isDropdown: true,
                  dropdownType: DROPDOWN_TYPES.MAPPINGS,
                  dropdownItems:
                    integrationMappings?.map((mapping) => ({
                      label: mapping.label,
                      href: `/settings/company/${user?.company?.id}/integrations/${integrationName}/${mapping.id}`,
                    })) || [],
                });
              }
            } else {
              items.push({
                label: 'Mappings',
                href: `/settings/company/${user?.company?.id}/integrations/${integrationName}/mappings`,
              });
            }
          } else if (path.includes('/admin')) {
            items.push({
              label: 'Admin',
              href: '/settings/admin',
            });
          }

          return items;
        }
      } else if (path.includes('/admin')) {
        items.push({
          label: 'Admin',
          href: '/settings/admin',
        });
      }

      return items;
    }

    // Handle company navigation
    if (user?.is_superuser) {
      items.push({
        label: 'Companies',
        href: '/settings/company',
      });
    } else {
      // Use the first available route for non-superusers
      const firstRoute = settingsRoutes[0];
      items.push({
        label: 'Settings',
        href: firstRoute?.path ?? '/settings',
      });
    }

    // Add current company with dropdown for superusers
    if (companyId) {
      const companies = Array.isArray(companyData) ? companyData : companyData?.results || [];
      const company = companies.find((c) => c.id.toString() === companyId) || currentCompanyData;
      items.push({
        label: company?.name || 'Loading...',
        href: `/settings/company/${companyId}`,
        ...(user?.is_superuser && {
          isDropdown: true,
          dropdownType: DROPDOWN_TYPES.COMPANIES,
          dropdownItems:
            companies
              .map((company) => ({
                label: company.name,
                href: `/settings/company/${company.id}`,
                isActive: company.id.toString() === companyId,
              }))
              .sort((a, b) => a.label.localeCompare(b.label)) || [],
        }),
      });
    }

    // Add user if viewing user details
    if (userId) {
      const currentUser = userData?.results?.find((u) => u.id.toString() === userId);
      items.push({
        label: currentUser ? `${currentUser.first_name} ${currentUser.last_name}` : 'Loading...',
        href: `/settings/company/${companyId}/users/${userId}`,
        isDropdown: true,
        dropdownType: DROPDOWN_TYPES.USERS,
        dropdownItems:
          userData?.results
            ?.map((user) => ({
              label: `${user.first_name} ${user.last_name}`,
              href: `/settings/company/${companyId}/users/${user.id}`,
              isActive: user.id.toString() === userId,
            }))
            .sort((a, b) => a.label.localeCompare(b.label)) || [],
      });
    }

    return items;
  }, [
    user,
    companyId,
    userId,
    companyData,
    currentCompanyData,
    userData,
    pathname,
    mappings,
    settingsRoutes,
    canViewCompanyUsers,
  ]);

  const breadcrumbs = useMemo(() => getBreadcrumbs(), [getBreadcrumbs]);

  return (
    <div
      className="h-12 flex flex-col bg-neutral-050 border-b border-neutral-200"
      data-testid="settings-breadcrumb"
    >
      <nav
        className="h-full flex items-center text-sm w-full px-4 py-2"
        aria-label="Breadcrumb"
      >
        <ol className="flex items-center gap-2">
          {breadcrumbs.map((item, index) => {
            const isLastItem = index === breadcrumbs.length - 1;
            return (
              <li
                key={`breadcrumb-${index}-${item.href}`}
                className="flex items-center gap-2"
                data-testid={`breadcrumb-item-${index}`}
              >
                {index > 0 && (
                  <div
                    className="flex items-center text-neutral-500"
                    aria-hidden="true"
                  >
                    /
                  </div>
                )}
                {item.isDropdown && item.dropdownType ? (
                  <Dropdown
                    type={item.dropdownType}
                    item={item}
                    items={item.dropdownItems || []}
                    isActive={item.isActive}
                    onSearchChange={item.dropdownType === DROPDOWN_TYPES.COMPANIES ? setCompanySearchTerm : item.dropdownType === DROPDOWN_TYPES.USERS ? setUserSearchTerm : undefined}
                    onSelect={(href) => {
                      if (item.dropdownType === DROPDOWN_TYPES.MAPPINGS) {
                        const mappingId = href.split('/').pop();
                        const mapping = mappings?.find(
                          (m) => m.id.toString() === mappingId
                        );
                        if (mapping) {
                          setCurrentMapping(mapping);
                        }
                      }
                      router.push(href);
                    }}
                  />
                ) : (
                  <BreadcrumbLink
                    href={item.href}
                    label={item.label}
                    isActive={isLastItem || item.isActive}
                    onClick={item.onClick}
                  />
                )}
              </li>
            );
          })}
        </ol>
      </nav>
    </div>
  );
};

export default SettingsBreadcrumbs;
